import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridCsvExportOptions,
  useGridApiContext,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface EditableDataGridProps<T extends { id: string }> {
  data: T[];
  columns: GridColDef[];
  onDataChange: (data: T[]) => void;
  onAddRow?: () => T;
  onDeleteRows?: (ids: string[]) => void;
  onExportJson?: (data: T[]) => void;
  selectable?: boolean;
  editable?: boolean;
  title?: string;
  loading?: boolean;
  height?: number | string;
  rowHeight?: number;
  showToolbar?: boolean;
  customActions?: React.ReactNode;
}

function CustomToolbar<T extends { id: string }>({
  data,
  selectedRows,
  onAddRow,
  onDeleteRows,
  onExportJson,
  customActions,
}: {
  data: T[];
  selectedRows: string[];
  onAddRow?: () => T;
  onDeleteRows?: (ids: string[]) => void;
  onExportJson?: (data: T[]) => void;
  customActions?: React.ReactNode;
}) {

  const apiRef = useGridApiContext();

  return (
    <GridToolbarContainer sx={{ p: 1, gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => apiRef.current.exportDataAsCsv()}
      >
        Export CSV
      </Button>
      
      {onAddRow && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddRow()}
          variant="contained"
        >
          Add Row
        </Button>
      )}

      {onDeleteRows && selectedRows.length > 0 && (
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => onDeleteRows(selectedRows)}
          color="error"
        >
          Delete Selected ({selectedRows.length})
        </Button>
      )}

      {customActions}
    </GridToolbarContainer>
  );
}

export function EditableDataGrid<T extends { id: string }>({
  data,
  columns,
  onDataChange,
  onAddRow,
  onDeleteRows,
  onExportJson,
  selectable = true,
  editable = true,
  title,
  loading = false,
  height = 400,
  rowHeight = 65,
  showToolbar = true,
  customActions,
}: EditableDataGridProps<T>) {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);

  const handleProcessRowUpdate = useCallback(
    (newRow: T) => {
      const updatedData = data.map(row => 
        row.id === newRow.id ? newRow : row
      );
      onDataChange(updatedData);
      return newRow;
    },
    [data, onDataChange]
  );

  const handleAddRow = useCallback(() => {
    if (onAddRow) {
      const newRow = onAddRow();
      onDataChange([...data, newRow]);
    }
  }, [data, onAddRow, onDataChange]);

  const handleDeleteRows = useCallback(
    (ids: string[]) => {
      if (onDeleteRows) {
        onDeleteRows(ids);
      } else {
        const filteredData = data.filter(row => !ids.includes(row.id));
        onDataChange(filteredData);
      }
      setSelectedRows([]);
    },
    [data, onDataChange, onDeleteRows]
  );

  const enhancedColumns: GridColDef[] = columns.map(col => ({
    ...col,
    editable: editable && col.editable !== false,
  }));

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Box sx={{ mb: 2 }}>
          <h3>{title}</h3>
        </Box>
      )}
      
      <DataGrid
        rows={data}
        columns={enhancedColumns}
        loading={loading}
        checkboxSelection={selectable}
        disableRowSelectionOnClick
        onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
          setSelectedRows(selection as string[]);
        }}
        rowSelectionModel={selectedRows}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Update error:', error)}
        autoHeight={height === 'auto'}
        sx={{
          height: height !== 'auto' ? height : undefined,
          '& .MuiDataGrid-cell': {
            padding: '8px',
          },
          '& .MuiDataGrid-row': {
            minHeight: `${rowHeight}px !important`,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '2px solid rgba(224, 224, 224, 1)',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-row .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
        }}
        slots={{
          toolbar: showToolbar ? CustomToolbar : undefined,
        }}
        slotProps={{
          toolbar: {
            data,
            selectedRows,
            onAddRow: onAddRow ? handleAddRow : undefined,
            onDeleteRows: handleDeleteRows,
            onExportJson,
            customActions,
          },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />
    </Box>
  );
}

// Utility function to create common column types
export const createColumns = {
  text: (field: string, headerName: string, width?: number): GridColDef => ({
    field,
    headerName,
    width: width || 150,
    editable: true,
  }),
  
  number: (field: string, headerName: string, width?: number): GridColDef => ({
    field,
    headerName,
    width: width || 100,
    type: 'number',
    editable: true,
  }),
  
  boolean: (field: string, headerName: string, width?: number): GridColDef => ({
    field,
    headerName,
    width: width || 90,
    type: 'boolean',
    editable: true,
  }),
  
  select: (
    field: string,
    headerName: string,
    options: string[],
    width?: number
  ): GridColDef => ({
    field,
    headerName,
    width: width || 150,
    editable: true,
    type: 'singleSelect',
    valueOptions: options,
  }),
  
  chip: (
    field: string,
    headerName: string,
    colorMap?: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'>,
    width?: number
  ): GridColDef => ({
    field,
    headerName,
    width: width || 120,
    renderCell: (params: GridRenderCellParams) => {
      const value = params.value as string;
      const color = colorMap?.[value] || 'default';
      return <Chip label={value} size="small" color={color} />;
    },
  }),
  
  multiline: (field: string, headerName: string, width?: number): GridColDef => ({
    field,
    headerName,
    width: width || 250,
    editable: true,
    renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
        {params.value}
      </Box>
    ),
  }),
  
  action: (
    field: string,
    headerName: string,
    actions: Array<{
      icon: React.ReactNode;
      tooltip: string;
      onClick: (row: any) => void;
    }>,
    width?: number
  ): GridColDef => ({
    field,
    headerName,
    width: width || 120,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => (
      <Box>
        {actions.map((action, index) => (
          <Tooltip key={index} title={action.tooltip}>
            <IconButton
              size="small"
              onClick={() => action.onClick(params.row)}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    ),
  }),
};
